import { useState } from 'react';

import { ReviewPanel, AgentEmptyState } from '../ReviewPanel';
import { CheckpointActions } from '../CheckpointActions';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { postOraClarification } from '@/lib/api';
import type { AgentOutputRow } from '@/hooks/useJobProgress';

interface OraPayload {
  needs_clarification: boolean;
  clarification_questions?: { id: string; question: string; reason?: string }[];
  intent_descriptor?: {
    primary_goal?: string;
    secondary_goals?: string[];
    target_audience?: { primary_persona?: string; secondary_persona?: string | null; age_bracket?: string };
    tone?: string;
    style_keywords?: string[];
    inspirations?: string[];
    typography_brief?: string;
    color_brief?: string;
    layout_brief?: string;
    functional_requirements?: string[];
    information_architecture_hints?: string[];
    success_criteria?: string[];
  };
}

interface OraPanelProps {
  jobId: string;
  output: AgentOutputRow | undefined;
  isRunning?: boolean;
}

export function OraPanel({ jobId, output, isRunning }: OraPanelProps) {
  if (!output) {
    return (
      <ReviewPanel agent="ora" index={1}>
        <AgentEmptyState agent="ora" isRunning={isRunning} />
      </ReviewPanel>
    );
  }

  const payload = output.payload as OraPayload;
  const isProbe = payload.needs_clarification === true;

  return (
    <ReviewPanel
      agent="ora"
      index={1}
      iteration={output.iteration}
      reviewStatus={output.reviewStatus}
      subtitle="Intent"
    >
      {isProbe ? (
        <ClarificationForm jobId={jobId} output={output} payload={payload} />
      ) : (
        <IntentView jobId={jobId} output={output} payload={payload} />
      )}
    </ReviewPanel>
  );
}

function IntentView({
  jobId,
  output,
  payload,
}: {
  jobId: string;
  output: AgentOutputRow;
  payload: OraPayload;
}) {
  const intent = payload.intent_descriptor ?? {};
  return (
    <div className="space-y-6">
      <Section label="Primary goal">
        <p className="text-body text-ink">{intent.primary_goal || '—'}</p>
      </Section>

      {intent.secondary_goals && intent.secondary_goals.length > 0 && (
        <Section label="Secondary goals">
          <List items={intent.secondary_goals} />
        </Section>
      )}

      <Section label="Audience">
        <p className="text-body text-ink">
          {intent.target_audience?.primary_persona || '—'}
          {intent.target_audience?.secondary_persona
            ? <> · <span className="text-graphite">{intent.target_audience.secondary_persona}</span></>
            : null}
          {intent.target_audience?.age_bracket
            ? <> <span className="font-mono text-[11px] text-graphite">[{intent.target_audience.age_bracket}]</span></>
            : null}
        </p>
      </Section>

      <Section label="Tone">
        <p className="text-body text-ink">{intent.tone || '—'}</p>
      </Section>

      {intent.style_keywords && intent.style_keywords.length > 0 && (
        <Section label="Style keywords">
          <KeywordRow words={intent.style_keywords} />
        </Section>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        <Section label="Typography brief"><p className="text-body text-ink">{intent.typography_brief || '—'}</p></Section>
        <Section label="Colour brief"><p className="text-body text-ink">{intent.color_brief || '—'}</p></Section>
        <Section label="Layout brief"><p className="text-body text-ink">{intent.layout_brief || '—'}</p></Section>
      </div>

      {intent.functional_requirements && intent.functional_requirements.length > 0 && (
        <Section label="Functional requirements"><List items={intent.functional_requirements} /></Section>
      )}
      {intent.information_architecture_hints && intent.information_architecture_hints.length > 0 && (
        <Section label="Information architecture"><List items={intent.information_architecture_hints} /></Section>
      )}
      {intent.success_criteria && intent.success_criteria.length > 0 && (
        <Section label="Success criteria"><List items={intent.success_criteria} /></Section>
      )}

      <CheckpointActions
        jobId={jobId}
        outputId={output._id}
        agent="ora"
        disabled={output.reviewStatus !== 'awaiting_user'}
      />
    </div>
  );
}

function ClarificationForm({
  jobId,
  output,
  payload,
}: {
  jobId: string;
  output: AgentOutputRow;
  payload: OraPayload;
}) {
  const { token } = useAuth();
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const questions = payload.clarification_questions ?? [];

  const allAnswered = questions.every((q) => (answers[q.id] ?? '').trim().length > 0);

  const submit = async () => {
    if (!token || !allAnswered) return;
    setSubmitting(true);
    try {
      await postOraClarification(token, {
        job_id: jobId,
        output_id: output._id,
        answers,
      });
      toast({ title: 'Answers submitted. ORA will re-run.' });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-body text-graphite">
        ORA needs a little more clarity before producing the intent descriptor. Answer the questions
        below and ORA will rerun automatically.
      </p>
      <div className="space-y-4">
        {questions.map((q) => (
          <div key={q.id} className="space-y-1.5">
            <Label htmlFor={`q-${q.id}`} className="text-ink">{q.question}</Label>
            {q.reason ? <p className="text-[12px] text-graphite italic">{q.reason}</p> : null}
            <Textarea
              id={`q-${q.id}`}
              value={answers[q.id] ?? ''}
              onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
              rows={3}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end border-t border-ink/10 pt-4">
        <Button onClick={submit} disabled={!allAnswered || submitting} variant="accent">
          {submitting ? 'Submitting…' : 'Submit answers'}
        </Button>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="smallcaps text-graphite">{label}</p>
      {children}
    </div>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2 text-body text-ink">
          <span className="text-graphite font-mono text-[11px] tabular-nums w-6">{String(i + 1).padStart(2, '0')}</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function KeywordRow({ words }: { words: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {words.map((w, i) => (
        <span key={i} className="rounded-sm border border-ink/15 bg-paper-2 px-2 py-0.5 font-mono text-[11px] text-ink/80">
          {w}
        </span>
      ))}
    </div>
  );
}
